/**
 * Sales Intelligence form — simple loading state while the report generates.
 * Does not touch VersionChecker AJAX (.btn--check-version).
 */

const OVERLAY_ID = 'ns-si-loading-overlay';
const DEFAULT_LABEL = 'Generating your report…';

function resolveLabel(section) {
  return (section?.getAttribute('data-ns-si-loading-title') || '').trim() || DEFAULT_LABEL;
}

function ensureOverlay(label) {
  let overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.className = 'ns-si-loading';
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    overlay.setAttribute('aria-busy', 'true');
    overlay.hidden = true;
    overlay.innerHTML = `
      <div class="ns-si-loading__box">
        <span class="ns-si-loading__spinner" aria-hidden="true"></span>
        <span class="ns-si-loading__label"></span>
      </div>
    `;
    document.body.appendChild(overlay);
  }

  const labelEl = overlay.querySelector('.ns-si-loading__label');
  if (labelEl) {
    labelEl.textContent = label;
  }

  return overlay;
}

function showLoading(section) {
  const overlay = ensureOverlay(resolveLabel(section));
  overlay.hidden = false;
  document.body.classList.add('ns-si-loading-active');
}

function hideLoading() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (overlay) {
    overlay.hidden = true;
  }
  document.body.classList.remove('ns-si-loading-active');
}

function isValidWebsiteInput(input) {
  if (!input) {
    return true;
  }
  const value = (input.value || '').trim();
  if (value === '') {
    return false;
  }
  if (/^https?:\/\//i.test(value)) {
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  }
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+(\/.*)?$/i.test(value);
}

function bindSalesIntelligenceForms() {
  document.querySelectorAll('form[data-ns-si-audit-form="1"]').forEach((form) => {
    if (form.getAttribute('data-ns-si-bound') === '1') {
      return;
    }
    form.setAttribute('data-ns-si-bound', '1');

    form.addEventListener('submit', (event) => {
      const section = form.closest('.sales-intelligence-form');
      const websiteInput = form.querySelector('input[name="website"]');

      if (websiteInput) {
        const value = (websiteInput.value || '').trim();
        if (!isValidWebsiteInput(websiteInput)) {
          event.preventDefault();
          websiteInput.setCustomValidity(
            value === ''
              ? websiteInput.validationMessage || 'Please enter a website URL.'
              : 'Please enter a valid website URL.',
          );
          websiteInput.reportValidity();
          return;
        }
        websiteInput.setCustomValidity('');
      }

      const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('ns-si-form-submitting');
      }

      showLoading(section);
    });
  });
}

function clearLoadingOnReportPage() {
  if (document.querySelector('.ns-si-audit')) {
    hideLoading();
  }
}

function showUnlockThanks(root, title, message) {
  const body = root.querySelector('[data-ns-si-unlock-body="1"]') || root;
  body.innerHTML = `
    <div class="ns-si-unlock__icon ns-si-unlock__icon--thanks" aria-hidden="true">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>
    <h3 class="ns-si-unlock__title">${title}</h3>
    <p class="ns-si-unlock__intro ns-si-unlock__thanks">${message}</p>
  `;
  root.classList.add('ns-si-unlock--thanks');
  root.setAttribute('aria-live', 'polite');
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getBlockedEmailDomains(root) {
  const spamEl = root?.querySelector('#ns-si-spam-email-text') || document.getElementById('ns-si-spam-email-text');
  const spamText = spamEl?.textContent?.trim();
  if (!spamText) {
    return [];
  }

  return spamText
    .split(',')
    .map((domain) => domain.trim().toLowerCase())
    .filter(Boolean);
}

function isBlockedUnlockEmail(root, email) {
  const value = String(email || '').trim().toLowerCase();
  if (!value || !value.includes('@')) {
    return true;
  }

  const local = value.slice(0, value.lastIndexOf('@'));
  const domain = value.slice(value.lastIndexOf('@') + 1);

  if (['example.com', 'example.org', 'example.net', 'test.com', 'localhost'].includes(domain)) {
    return true;
  }

  if (/^(test|fake|spam|temp|trash|dummy|noreply|no-reply)([._-]|$)/.test(local)) {
    return true;
  }

  const blockedDomains = getBlockedEmailDomains(root);
  return blockedDomains.some(
    (blocked) => domain === blocked
      || domain.endsWith(`.${blocked}`)
      || domain.startsWith(`${blocked}.`),
  );
}

function initUnlockStartedAt(form) {
  const startedInput = form.querySelector('[data-ns-si-unlock-started="1"]');
  if (startedInput && !startedInput.value) {
    startedInput.value = String(Math.floor(Date.now() / 1000));
  }
}

function bindUnlockForms() {
  document.querySelectorAll('[data-ns-si-unlock="1"] form.ns-si-unlock__form').forEach((form) => {
    if (form.getAttribute('data-ns-si-unlock-bound') === '1') {
      return;
    }
    form.setAttribute('data-ns-si-unlock-bound', '1');
    initUnlockStartedAt(form);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const root = form.closest('[data-ns-si-unlock="1"]');
      const emailInput = form.querySelector('input[type="email"], input[name*="[email]"]');
      if (emailInput && !emailInput.checkValidity()) {
        emailInput.reportValidity();
        return;
      }

      const email = (emailInput?.value || '').trim();
      const honeypot = (form.querySelector('[name="ns_si_hp"]')?.value || '').trim();
      if (honeypot !== '') {
        if (root) {
          showUnlockThanks(
            root,
            escapeHtml(document.documentElement.lang?.startsWith('de') ? 'Vielen Dank!' : 'Thank you!'),
            escapeHtml(document.documentElement.lang?.startsWith('de')
              ? 'Wir haben Ihre Anfrage erhalten. Bitte prüfen Sie Ihr Postfach.'
              : 'We received your request. Please check your inbox.'),
          );
        }
        return;
      }

      if (isBlockedUnlockEmail(root, email)) {
        if (root) {
          showUnlockThanks(
            root,
            'Something went wrong',
            'Please use a business or personal email address. Temporary email providers are not accepted.',
          );
        }
        return;
      }

      const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = true;
      }

      try {
        const formData = new FormData(form);
        // Mirror VersionChecker AJAX: ensure Extbase plugin namespace fields are present.
        const email = (emailInput?.value || '').trim();
        if (email !== '') {
          formData.set('tx_nsthemenitsan_salesintelligenceform[email]', email);
          formData.set('email', email);
        }

        ['domain', 'reportType', 'status', 'summary', 'score'].forEach((key) => {
          const el = form.querySelector(`[name*="[${key}]"], [name="${key}"]`);
          const value = (el?.value || formData.get(`tx_nsthemenitsan_salesintelligenceform[${key}]`) || formData.get(key) || '').toString();
          if (value !== '') {
            formData.set(`tx_nsthemenitsan_salesintelligenceform[${key}]`, value);
            formData.set(key, value);
          }
        });

        formData.set('tx_nsthemenitsan_salesintelligenceform[action]', 'unlock');
        formData.set('tx_nsthemenitsan_salesintelligenceform[controller]', 'SalesIntelligenceForm');

        const startedAt = form.querySelector('[data-ns-si-unlock-started="1"]')?.value;
        if (startedAt) {
          formData.set('_startedAt', startedAt);
          formData.set('tx_nsthemenitsan_salesintelligenceform[_startedAt]', startedAt);
        }

        ['ns_si_hp'].forEach((field) => {
          const el = form.querySelector(`[name="${field}"]`);
          const value = (el?.value || '').trim();
          if (value !== '') {
            formData.set(field, value);
          }
        });

        const response = await fetch(form.action, {
          method: 'POST',
          body: formData,
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
        });

        const raw = await response.text();
        let data = {};
        try {
          data = JSON.parse(raw);
        } catch {
          data = {};
        }

        if (!response.ok || typeof data.success === 'undefined') {
          if (submitBtn) {
            submitBtn.disabled = false;
          }
          if (root) {
            showUnlockThanks(
              root,
              'Something went wrong',
              'Please try again in a moment.',
            );
          }
          return;
        }

        const title = escapeHtml(data.title || (data.success ? 'Thank you!' : 'Something went wrong'));
        const message = escapeHtml(
          data.message
            || (data.success
              ? 'We received your request and will follow up by email.'
              : 'Please check your email address and try again.'),
        );

        if (root) {
          showUnlockThanks(root, title, message);
        }
      } catch {
        if (submitBtn) {
          submitBtn.disabled = false;
        }
        if (root) {
          showUnlockThanks(
            root,
            'Something went wrong',
            'Please try again in a moment.',
          );
        }
      }
    });
  });
}

export const initSalesIntelligenceForm = () => {
  bindSalesIntelligenceForms();
  bindUnlockForms();
  clearLoadingOnReportPage();
};

initSalesIntelligenceForm();

document.addEventListener('barba:after-enter', initSalesIntelligenceForm);

window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    hideLoading();
    document.querySelectorAll('form[data-ns-si-audit-form="1"] button[type="submit"]').forEach((btn) => {
      btn.disabled = false;
      btn.classList.remove('ns-si-form-submitting');
    });
  }
});
